import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  FaFileExcel, FaFileWord, FaFilePdf, FaFilePowerpoint,
  FaFileArchive, FaFileAlt, FaFileImage,
} from 'react-icons/fa';
import { MdClose, MdFileDownload, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import axiosInstance from '../services/axiosInstance';
import { downloadFile, showDownloadProgress, hideDownloadProgress } from '../utils/downloadFile';
import { ClipLoader } from 'react-spinners';

// ─── Icon aliases ──────────────────────────────────────────────────────────────
const FilePdfIcon        = FaFilePdf as any;
const FileExcelIcon      = FaFileExcel as any;
const FileWordIcon       = FaFileWord as any;
const FilePowerpointIcon = FaFilePowerpoint as any;
const FileArchiveIcon    = FaFileArchive as any;
const FileAltIcon        = FaFileAlt as any;
const FileImageIcon      = FaFileImage as any;
const CloseIcon          = MdClose as any;
const DownloadIcon       = MdFileDownload as any;
const PrevIcon           = MdChevronLeft as any;
const NextIcon           = MdChevronRight as any;

const isMobileDevice = () => /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AttachmentFile {
  url: string;
  name: string;
  /** If provided, download uses axiosInstance.get(axiosPath) — for auth-gated files. */
  axiosPath?: string;
}

interface Props {
  items: AttachmentFile[];
  thumbnailSize?: number;
}

type FileKind = 'image' | 'pdf' | 'excel' | 'word' | 'ppt' | 'archive' | 'file';
type ModalState =
  | null
  | { kind: 'image'; galleryIdx: number }
  | { kind: 'pdf';   item: AttachmentFile }
  | { kind: 'file';  item: AttachmentFile };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getKind(name: string): FileKind {
  const ext = (name?.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  return 'file';
}

function KindIcon({ kind, size }: { kind: FileKind; size: number }) {
  switch (kind) {
    case 'pdf':     return <FilePdfIcon        size={size} color="#EF4444" />;
    case 'excel':   return <FileExcelIcon      size={size} color="#10B981" />;
    case 'word':    return <FileWordIcon       size={size} color="#3B82F6" />;
    case 'ppt':     return <FilePowerpointIcon size={size} color="#F59E0B" />;
    case 'archive': return <FileArchiveIcon    size={size} color="#6B7280" />;
    case 'image':   return <FileImageIcon      size={size} color="#3B82F6" />;
    default:        return <FileAltIcon        size={size} color="#9CA3AF" />;
  }
}

function ZoomBar({ zoom, set }: { zoom: number; set: React.Dispatch<React.SetStateAction<number>> }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '5px 14px' }}>
      <button onClick={() => set(z => Math.max(0.5, z - 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>−</button>
      <span style={{ color: 'white', fontSize: 13, minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
      <button onClick={() => set(z => Math.min(3, z + 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>+</button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const AttachmentViewer: React.FC<Props> = ({ items, thumbnailSize = 88 }) => {
  const [modal, setModal]     = useState<ModalState>(null);
  const [imgZoom, setImgZoom] = useState(1);
  const [imgTx, setImgTx]     = useState({ x: 0, y: 0 });
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfPages, setPdfPages]         = useState<string[]>([]);
  const [pdfLoading, setPdfLoading]     = useState(false);
  const [pdfError, setPdfError]         = useState<string | null>(null);
  const [pdfPasswordPrompt, setPdfPasswordPrompt]       = useState(false);
  const [pdfPasswordIncorrect, setPdfPasswordIncorrect] = useState(false);
  const [pdfPasswordInput, setPdfPasswordInput]         = useState('');

  const pinchRef          = useRef<{ startDist: number; startZoom: number } | null>(null);
  const dragRef           = useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);
  const lastTapRef        = useRef(0);
  const pdfTaskRef        = useRef<any>(null);
  const pendingPasswordCb = useRef<((p: string) => void) | null>(null);

  const imageItems = items.filter(it => getKind(it.name) === 'image');

  // Load PDF on mobile when PDF modal opens
  useEffect(() => {
    if (modal?.kind !== 'pdf') {
      setPdfPages([]);
      setPdfError(null);
      setPdfZoom(1);
      setPdfPasswordPrompt(false);
      setPdfPasswordIncorrect(false);
      setPdfPasswordInput('');
      pendingPasswordCb.current = null;
      if (pdfTaskRef.current) { pdfTaskRef.current.destroy?.(); pdfTaskRef.current = null; }
      return;
    }
    if (isMobileDevice()) loadPdf(modal.item);
    else setPdfZoom(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.kind === 'pdf' ? (modal as any).item.url : null]);

  // Reset image zoom when image item changes
  useEffect(() => {
    setImgZoom(1);
    setImgTx({ x: 0, y: 0 });
  }, [modal?.kind === 'image' ? (modal as any).galleryIdx : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPdf = useCallback(async (item: AttachmentFile) => {
    setPdfPages([]);
    setPdfLoading(true);
    setPdfError(null);
    setPdfPasswordPrompt(false);
    setPdfPasswordIncorrect(false);
    setPdfPasswordInput('');
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      let data: ArrayBuffer;
      if (item.axiosPath) {
        const res = await axiosInstance.get(item.axiosPath, { responseType: 'arraybuffer' });
        data = res.data;
      } else {
        data = await (await fetch(item.url)).arrayBuffer();
      }

      const task = pdfjsLib.getDocument({ data });
      pdfTaskRef.current = task;

      (task as any).onPassword = (updatePassword: (p: string) => void, reason: number) => {
        pendingPasswordCb.current = updatePassword;
        setPdfPasswordIncorrect(reason === 2);
        setPdfPasswordPrompt(true);
        setPdfLoading(false);
      };

      const pdf = await task.promise;
      pdfTaskRef.current = null;

      const pages: string[] = [];
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const vp = page.getViewport({ scale: dpr });
        const canvas = document.createElement('canvas');
        canvas.width = vp.width; canvas.height = vp.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        pages.push(canvas.toDataURL('image/jpeg', 0.88));
      }
      setPdfPages(pages);
    } catch (err: any) {
      if (err?.name === 'PasswordException') return;
      setPdfError('Gagal memuat PDF. Gunakan tombol unduh untuk membukanya.');
    } finally {
      setPdfLoading(false);
    }
  }, []);

  const handlePasswordSubmit = useCallback(() => {
    if (!pendingPasswordCb.current || !pdfPasswordInput.trim()) return;
    const cb = pendingPasswordCb.current;
    pendingPasswordCb.current = null;
    setPdfPasswordPrompt(false);
    setPdfLoading(true);
    cb(pdfPasswordInput.trim());
    setPdfPasswordInput('');
  }, [pdfPasswordInput]);

  const handleDownload = useCallback(async (item: AttachmentFile) => {
    if (item.axiosPath) {
      try {
        showDownloadProgress(0, 'Mengunduh file...');
        const res = await axiosInstance.get(item.axiosPath, {
          responseType: 'blob',
          onDownloadProgress: (evt: any) => {
            if (evt.total) showDownloadProgress(Math.round((evt.loaded / evt.total) * 90), 'Mengunduh file...');
          },
        });
        const blob = new Blob([res.data]);
        if (Capacitor.isNativePlatform()) {
          showDownloadProgress(92, 'Menyimpan file...');
          const base64 = await new Promise<string>((res, rej) => {
            const r = new FileReader();
            r.onloadend = () => res((r.result as string).split(',')[1]);
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          await Filesystem.writeFile({ path: item.name, data: base64, directory: Directory.Documents, recursive: true });
          showDownloadProgress(100, 'File tersimpan ✓');
          hideDownloadProgress('File tersimpan ✓');
        } else {
          showDownloadProgress(100, 'Mengunduh...');
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl; a.download = item.name; a.style.display = 'none';
          document.body.appendChild(a); a.click();
          hideDownloadProgress('Download selesai ✓');
          setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(objectUrl); }, 10000);
        }
      } catch {
        hideDownloadProgress('Download gagal');
        downloadFile(item.url, item.name);
      }
    } else {
      downloadFile(item.url, item.name);
    }
  }, []);

  const close = useCallback(() => setModal(null), []);

  if (items.length === 0) return null;

  // ─── Thumbnail grid ──────────────────────────────────────────────────────────

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {items.map((item, idx) => {
          const kind = getKind(item.name);
          const shortName = item.name.length > 15 ? item.name.slice(0, 13) + '…' : item.name;
          const onClick = () => {
            if (kind === 'image') {
              const galleryIdx = imageItems.findIndex(i => i.url === item.url);
              setModal({ kind: 'image', galleryIdx: galleryIdx >= 0 ? galleryIdx : 0 });
            } else if (kind === 'pdf') {
              setModal({ kind: 'pdf', item });
            } else {
              setModal({ kind: 'file', item });
            }
          };
          return (
            <div
              key={idx}
              onClick={onClick}
              style={{
                width: thumbnailSize,
                minHeight: thumbnailSize,
                borderRadius: 10,
                border: '1.5px solid #E5E7EB',
                backgroundColor: '#FAFAFA',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: kind === 'image' ? 'flex-start' : 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                padding: kind === 'image' ? 0 : '10px 6px 8px',
                gap: kind === 'image' ? 0 : 6,
              }}
            >
              {kind === 'image' ? (
                <img src={item.url} alt={item.name} style={{ width: '100%', height: thumbnailSize, objectFit: 'cover', display: 'block' }} />
              ) : (
                <>
                  <KindIcon kind={kind} size={28} />
                  <span style={{ fontSize: 10, color: '#6B7280', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>{shortName}</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Image lightbox ───────────────────────────────────────────────────── */}
      {modal?.kind === 'image' && (() => {
        const galleryIdx = (modal as any).galleryIdx as number;
        const cur = imageItems[galleryIdx];
        const hasPrev = galleryIdx > 0;
        const hasNext = galleryIdx < imageItems.length - 1;

        const onTouchStart = (e: React.TouchEvent) => {
          e.stopPropagation();
          if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            pinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: imgZoom };
            dragRef.current = null;
          } else if (e.touches.length === 1) {
            dragRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, startTx: imgTx.x, startTy: imgTx.y };
          }
        };
        const onTouchMove = (e: React.TouchEvent) => {
          e.stopPropagation();
          if (e.touches.length === 2 && pinchRef.current) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const z = Math.min(5, Math.max(1, (Math.hypot(dx, dy) / pinchRef.current.startDist) * pinchRef.current.startZoom));
            setImgZoom(z);
            if (z <= 1) setImgTx({ x: 0, y: 0 });
          } else if (e.touches.length === 1 && dragRef.current && imgZoom > 1) {
            setImgTx({ x: dragRef.current.startTx + e.touches[0].clientX - dragRef.current.startX, y: dragRef.current.startTy + e.touches[0].clientY - dragRef.current.startY });
          }
        };
        const onTouchEnd = (e: React.TouchEvent) => {
          e.stopPropagation();
          pinchRef.current = null; dragRef.current = null;
          if (e.changedTouches.length === 1) {
            const now = Date.now(), dt = now - lastTapRef.current;
            lastTapRef.current = now;
            if (dt < 300 && dt > 0) {
              if (imgZoom > 1) { setImgZoom(1); setImgTx({ x: 0, y: 0 }); } else setImgZoom(2.5);
            }
          }
        };
        const nav = (dir: number) => (e: React.MouseEvent) => {
          e.stopPropagation();
          setModal({ kind: 'image', galleryIdx: galleryIdx + dir });
        };

        return (
          <div style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, zIndex: 9000, backgroundColor: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', display: 'flex', flexDirection: 'column' }} onClick={close}>
            <button onClick={close} style={{ position: 'absolute', top: 16, right: 16, zIndex: 1, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CloseIcon size={20} color="#fff" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleDownload(cur); }} style={{ position: 'absolute', top: 16, left: 16, zIndex: 1, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DownloadIcon size={18} color="#fff" />
            </button>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
              onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
            >
              <img
                src={cur.url} alt={cur.name}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', transform: `scale(${imgZoom}) translate(${imgTx.x / imgZoom}px, ${imgTx.y / imgZoom}px)`, transition: pinchRef.current ? 'none' : 'transform 0.15s ease' }}
              />
            </div>

            {hasPrev && <button onClick={nav(-1)} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PrevIcon size={24} color="#fff" /></button>}
            {hasNext && <button onClick={nav(1)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><NextIcon size={24} color="#fff" /></button>}

            {imageItems.length > 1 && (
              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.65)', fontSize: 12, background: 'rgba(0,0,0,0.4)', padding: '4px 12px', borderRadius: 20 }}>
                {galleryIdx + 1} / {imageItems.length}
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── PDF viewer ───────────────────────────────────────────────────────── */}
      {modal?.kind === 'pdf' && (() => {
        const item = (modal as any).item as AttachmentFile;
        const mobile = isMobileDevice();
        return (
          <div style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, zIndex: 9000, background: '#111827', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#1F2937', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
              <button onClick={() => handleDownload(item)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <DownloadIcon size={16} color="#fff" />
              </button>
              <button onClick={close} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <CloseIcon size={18} color="#fff" />
              </button>
            </div>

            {mobile ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' as any, background: '#1a1a2e' }}>
                  <div style={{ width: `${pdfZoom * 100}%` }}>
                    {pdfLoading && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
                        <ClipLoader size={36} color="#8B5CF6" />
                        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>Memuat PDF...</span>
                      </div>
                    )}
                    {pdfError && !pdfLoading && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '52px 24px', gap: 16 }}>
                        <span style={{ color: '#F87171', fontSize: 32 }}>⚠</span>
                        <span style={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', fontSize: 13 }}>{pdfError}</span>
                        <button onClick={() => loadPdf(item)} style={{ background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Coba Lagi</button>
                        <button onClick={() => handleDownload(item)} style={{ background: 'none', color: 'rgba(255,255,255,0.45)', border: 'none', fontSize: 13, cursor: 'pointer', padding: 0 }}>Download PDF</button>
                      </div>
                    )}
                    {!pdfLoading && pdfPages.map((src, i) => (
                      <img key={i} src={src} alt={`Halaman ${i + 1}`} style={{ width: '100%', display: 'block', marginBottom: i < pdfPages.length - 1 ? 4 : 0 }} />
                    ))}
                  </div>
                </div>
                {pdfPages.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', background: '#111827', flexShrink: 0 }}>
                    <ZoomBar zoom={pdfZoom} set={setPdfZoom} />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <div style={{ width: `${pdfZoom * 100}%`, height: `${pdfZoom * 100}%`, minHeight: '100%' }}>
                    <iframe src={`${item.url}#toolbar=1&navpanes=0`} style={{ width: '100%', height: '100%', border: 'none' }} title={item.name} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', background: '#111827', flexShrink: 0 }}>
                  <ZoomBar zoom={pdfZoom} set={setPdfZoom} />
                </div>
              </div>
            )}

            {/* Password prompt overlay — muncul di atas konten saat PDF berpassword */}
            {pdfPasswordPrompt && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ background: '#1F2937', borderRadius: 20, padding: 24, width: '100%', maxWidth: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>PDF Dilindungi Password</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 20 }}>
                    {pdfPasswordIncorrect ? 'Password salah. Coba lagi.' : 'Masukkan password untuk membuka PDF ini.'}
                  </div>
                  <input
                    type="password"
                    autoFocus
                    value={pdfPasswordInput}
                    onChange={e => setPdfPasswordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handlePasswordSubmit(); }}
                    placeholder="Password PDF..."
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: 'rgba(255,255,255,0.08)',
                      border: pdfPasswordIncorrect ? '1.5px solid #F87171' : '1.5px solid rgba(255,255,255,0.15)',
                      borderRadius: 12, padding: '13px 16px',
                      color: '#fff', fontSize: 15, outline: 'none', marginBottom: 16,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={close}
                      style={{ flex: 1, padding: '13px 0', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                    >Batal</button>
                    <button
                      onClick={handlePasswordSubmit}
                      disabled={!pdfPasswordInput.trim()}
                      style={{ flex: 2, padding: '13px 0', borderRadius: 12, background: pdfPasswordInput.trim() ? '#7C3AED' : 'rgba(124,58,237,0.4)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: pdfPasswordInput.trim() ? 'pointer' : 'not-allowed' }}
                    >Buka PDF</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Other file modal (download) ──────────────────────────────────────── */}
      {modal?.kind === 'file' && (() => {
        const item = (modal as any).item as AttachmentFile;
        const kind = getKind(item.name);
        return (
          <div style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, zIndex: 9000, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={close}>
            <div style={{ background: '#1F2937', borderRadius: 20, padding: 28, width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }} onClick={e => e.stopPropagation()}>
              <KindIcon kind={kind} size={52} />
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14, textAlign: 'center', wordBreak: 'break-all' }}>{item.name}</span>
              <button
                onClick={() => handleDownload(item)}
                style={{ width: '100%', background: '#7C3AED', border: 'none', borderRadius: 12, padding: '14px 0', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <DownloadIcon size={18} color="#fff" /> Unduh File
              </button>
              <button onClick={close} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer' }}>Tutup</button>
            </div>
          </div>
        );
      })()}
    </>
  );
};
